import { NavBar } from "@/components/ui";
import { Button } from "@/components/ui/button";
import { Textfield } from "@/components/ui/input/textfield";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useFormik } from "formik";
import * as Yup from "yup";

interface ContactUsViewProps {
  title?: string;
  description?: string;
  phone?: string;
  email?: string;
  web?: { label: string; url: string };
}

export const ContactUsView = ({
  title = "Contact Us",
  description = "We are available for questions, feedback, or collaboration opportunities. Let us know how we can help!",
  phone = "+234 7073685315",
  email = "info@qoonity.com",
  web = { label: "qoonity.com", url: "https://qoonity.com" },
}: ContactUsViewProps) => {
  const formik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      email: "",
      subject: "",
      message: "",
    },
    enableReinitialize: true,

    validationSchema: Yup.object({
      firstname: Yup.string().required("First name is required"),
      lastname: Yup.string().required("Last name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      subject: Yup.string().required("Subject is required"),
      message: Yup.string().required("Message is required"),
    }),
    onSubmit: (values) => {
      // Open default mail client with pre-filled info
      const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
        values.subject
      )}&body=${encodeURIComponent(`${values.message}`)}`;
      window.location.href = mailtoLink;
      setTimeout(() => {
        formik.setSubmitting(false);
      }, 500);
    },
  });

  return (
    <section>
      <NavBar />
      <div className="container mt-10">
        <div className="mx-auto flex max-w-7xl flex-col justify-between gap-10 lg:flex-row lg:gap-20">
          <div className="mx-auto flex max-w-sm flex-col justify-between gap-10">
            <div className="text-center lg:text-left">
              <h1 className="mb-2 text-5xl font-semibold lg:mb-1 lg:text-6xl">
                {title}
              </h1>
              <p className="text-muted-foreground">{description}</p>
            </div>
            <div className="mx-auto w-fit lg:mx-0">
              <h3 className="mb-6 text-center text-2xl font-semibold lg:text-left">
                Contact Details
              </h3>
              <ul className="ml-4 list-disc">
                <li>
                  <span className="font-bold">Phone: </span>
                  {phone}
                </li>
                <li>
                  <span className="font-bold">Email: </span>
                  <a href={`mailto:${email}`} className="underline">
                    {email}
                  </a>
                </li>
                <li>
                  <span className="font-bold">Web: </span>
                  <a href={web.url} target="_blank" className="underline">
                    {web.label}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <form
            onSubmit={formik.handleSubmit}
            className="mx-auto flex max-w-3xl flex-col gap-6 rounded-lg border border-gray-700 p-10"
          >
            <div className="flex gap-4">
              <div className="w-full">
                <Textfield
                  name="firstname"
                  label="First Name"
                  formik={formik}
                  placeholder="First Name"
                />
                {formik.touched.firstname && formik.errors.firstname && (
                  <p className="text-sm text-red-500">
                    {formik.errors.firstname}
                  </p>
                )}
              </div>

              <div className="w-full">
                <Textfield
                  name="lastname"
                  label="Last Name"
                  formik={formik}
                  placeholder="Last Name"
                />
                {formik.touched.lastname && formik.errors.lastname && (
                  <p className="text-sm text-red-500">
                    {formik.errors.lastname}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Textfield
                name="email"
                label="Email"
                formik={formik}
                placeholder="Email"
              />
              {formik.touched.email && formik.errors.email && (
                <p className="text-sm text-red-500">{formik.errors.email}</p>
              )}
            </div>

            <div>
              <Textfield
                name="subject"
                label="Subject"
                formik={formik}
                placeholder="Subject"
              />
              {formik.touched.subject && formik.errors.subject && (
                <p className="text-sm text-red-500">{formik.errors.subject}</p>
              )}
            </div>

            <div className="grid w-full gap-1.5">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                name="message"
                value={formik.values.message}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Type your message here..."
                className="bg-white text-black"
              />
              {formik.touched.message && formik.errors.message && (
                <p className="text-sm text-red-500">{formik.errors.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              onFocus={() => {
                formik.setFieldTouched("message", true);
              }}
              disabled={!formik.isValid}
            >
              {formik.isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};
